import { memo, useMemo } from 'react';
import { ResponsiveContainer, LineChart, Line } from 'recharts';
import { Skeleton } from 'antd';
import { Panel } from './Panel';
import { useWidgetData, aggregate, accentMap, currencyFmt, groupByCategory } from './shared';
import type { DashboardWidget } from '../../types/panelConfig';

interface Props {
  widget: Extract<DashboardWidget, { type: 'stat-sparkline' }>;
  sourceId: number | null | undefined;
}

function StatSparklineWidgetImpl({ widget, sourceId }: Props) {
  const { data, isLoading } = useWidgetData(sourceId, `stat-sparkline-${widget.reportCode}`);
  const { statField, aggregation, trendField, title, subtitle, color } = widget.config;

  const rows = data ?? [];
  const total = rows.length > 0 ? currencyFmt.format(aggregate(rows, statField, aggregation)) : '—';
  const stroke = accentMap[color || 'blue'] ?? accentMap.blue;

  const series = useMemo(() => {
    return groupByCategory(rows, trendField, statField, aggregation).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [rows, trendField, statField, aggregation]);

  return (
    <div data-testid="stat-sparkline-widget">
      <Panel title={title} subtitle={subtitle}>
        {isLoading ? (
          <Skeleton.Node active style={{ width: '100%', height: 60 }} />
        ) : (
          <>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                letterSpacing: '-0.01em',
              }}
            >
              {total}
            </div>
            <div style={{ height: 40, marginTop: 6 }}>
              {series.length > 1 && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series}>
                    <Line type="monotone" dataKey="value" stroke={stroke} strokeWidth={1.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}

export const StatSparklineWidget = memo(StatSparklineWidgetImpl);
